import { useCallback, useRef, useState, type RefObject } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ListTable, type ListColumn, type ListTableHandle } from '@/components/ListTable';
import { ListSearchControls } from '@/components/ListSearchControls/ListSearchControls';
import { ProvideSearchAPIContext, useSearchApiContext } from '@/components/ListSearchControls/contexts';
import type { SearchOptions } from '@/components/ListSearchControls/types';
import { useApi, useNavigate } from '@/lib/legacy';
import { portalUserSearchOptions } from '../../constants/searchData/searchOptions';
import { PortalUserForm, type PortalUserFormMode } from './PortalUserForm';
import { type PortalUser, PENDING, ROLE_LABELS, portalUserState } from './portalUser';

const LIST_PATH = '/admin/portal-users';

const userPath = (user: PortalUser) => `${LIST_PATH}/${user.userId}`;

const NO_ACCOUNT = 'Unknown user';

const userIdLine = (user: PortalUser) => <span className="block text-xs text-muted-foreground">User {user.userId}</span>;

// A grant whose account or portal role is missing gives no portal access, whatever the grants say.
const statusOf = (user: PortalUser): [string, 'success' | 'secondary' | 'warning'] => {
  const state = portalUserState(user);
  if (state === 'noPortalRole') {
    return ['No portal role', 'warning'];
  }
  if (state === 'noAccount') {
    return ['No account', 'warning'];
  }
  return user.active ? ['Active', 'success'] : ['No access', 'secondary'];
};

// The widths add up to 790px, which fits the list at 1280px beside a classic scrollbar, as on Applications (D36).
export const portalUserColumns: ListColumn<PortalUser>[] = [
  {
    id: 'name',
    header: 'NAME',
    size: 160,
    accessorFn: (user) => {
      const state = portalUserState(user);
      return state === 'pending' ? PENDING : state === 'noAccount' ? NO_ACCOUNT : user.fullName ?? '—';
    },
    cell: ({ row, getValue }) => {
      const state = portalUserState(row.original);
      return state === 'pending' || state === 'noAccount' ? (
        <>
          {getValue<string>()}
          {userIdLine(row.original)}
        </>
      ) : (
        getValue<string>()
      );
    },
  },
  {
    id: 'email',
    header: 'E-MAIL',
    size: 190,
    accessorFn: (user) => user.email ?? '',
    cell: ({ row }) =>
      portalUserState(row.original) === 'pending' ? (
        <Spinner aria-label="E-mail pending" className="text-muted-foreground" />
      ) : (
        row.original.email ?? '—'
      ),
  },
  {
    id: 'role',
    header: 'ROLE',
    size: 80,
    accessorFn: (user) => (user.role ? ROLE_LABELS[user.role] : ''),
    cell: ({ row }) =>
      portalUserState(row.original) === 'pending' ? (
        <Spinner aria-label="Role pending" className="text-muted-foreground" />
      ) : row.original.role ? (
        ROLE_LABELS[row.original.role]
      ) : (
        '—'
      ),
  },
  {
    id: 'customers',
    header: 'CUSTOMERS',
    size: 180,
    accessorFn: (user) => (user.customers.length ? user.customers.map((customer) => customer.name).join(', ') : '—'),
  },
  {
    id: 'status',
    header: 'STATUS',
    size: 90,
    accessorFn: (user) => statusOf(user)[0],
    cell: ({ getValue, row }) => <Badge variant={statusOf(row.original)[1]}>{getValue<string>()}</Badge>,
  },
  {
    id: 'added',
    header: 'ADDED',
    size: 90,
    accessorFn: (user) => (user.firstGrantedAt ? DateTime.fromISO(user.firstGrantedAt).toFormat('MM-dd-yyyy') : '—'),
  },
];

/**
 * Admin › Portal Users: every portal user, with the shared search toolbar and list table, as on
 * Applications. A row opens the user's form at /admin/portal-users/:userId, over the list; the list
 * keeps its search while the form is open, since the search lives in the list's own URL.
 */
export const PortalUsersView = () => {
  const listRef = useRef<ListTableHandle>(null);
  const { listPortalUsers } = useApi();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { userId } = useParams<{ userId?: string }>();
  const [adding, setAdding] = useState(false);

  // The list's own search, to go back to when the form closes.
  const listSearch = useRef(search);
  if (userId === undefined) {
    listSearch.current = search;
  }

  const getData = useCallback(
    // useAsyncSearch drops rows that share an id with an earlier one, and a row without an id
    // matches none: each row carries its user id as `id`.
    async (config: unknown) => ((await listPortalUsers(config)) as PortalUser[]).map((user) => ({ ...user, id: user.userId })),
    [listPortalUsers]
  );

  const closeForm = (open: boolean) => {
    if (open) return;
    if (userId !== undefined) {
      navigate.to({ pathname: LIST_PATH, search: listSearch.current });
    }
    setAdding(false);
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white px-4 pt-16 pb-6 md:px-[120px]">
      <ProvideSearchAPIContext
        searchPath={LIST_PATH}
        searchOptions={portalUserSearchOptions as SearchOptions}
        getData={getData}
        appSettingsKey="PortalUsersView"
        cache={false}
        keepRowsOnFailedPage
        listRef={listRef}
      >
        <div className="pb-4">
          <ListSearchControls
            heading="Portal Users"
            searchPlaceholder="Search by name or e-mail"
            headingRight={<Button onClick={() => setAdding(true)}>Add portal user</Button>}
            showDisplayTypeToggle={false}
          />
        </div>
        <PortalUsers tableRef={listRef} />
        {userId !== undefined ? (
          <OpenForm key={userId} mode={{ kind: 'edit', userId: Number(userId) }} onOpenChange={closeForm} />
        ) : adding ? (
          <OpenForm mode={{ kind: 'create' }} onOpenChange={closeForm} />
        ) : null}
      </ProvideSearchAPIContext>
    </div>
  );
};

/** The form, re-reading the list after every change it makes. */
const OpenForm = ({ mode, onOpenChange }: { mode: PortalUserFormMode; onOpenChange: (open: boolean) => void }) => {
  const { reload } = useSearchApiContext();
  return <PortalUserForm mode={mode} onOpenChange={onOpenChange} onChanged={reload} />;
};

const PortalUsers = ({ tableRef }: { tableRef: RefObject<ListTableHandle> }) => {
  const { data, loading, loadingFromCache, requestingMore, hasMore, failed, requestMoreData } = useSearchApiContext();
  return (
    <ListTable<PortalUser>
      ref={tableRef}
      rows={data as PortalUser[]}
      columns={portalUserColumns}
      rowPath={userPath}
      rowId={(user) => String(user.userId)}
      label="Portal users"
      emptyMessage="No portal users to show"
      loading={loading || requestingMore}
      fetching={loading || !!loadingFromCache || requestingMore}
      failed={failed}
      hasMore={hasMore}
      onLoadMore={requestMoreData}
    />
  );
};
